// /accept-invite — landing page for workspace-invitation emails.
//
// Three states:
//
//   1. Logged out: store the token in sessionStorage, redirect to
//      /login?next=/accept-invite. After sign-in or signup, we land
//      back here logged-in and pick up the stored token.
//      The signup flow ALSO auto-accepts pending invitations matching
//      the new user's email (server-side, see register handler), so
//      a fresh signup typically doesn't need to come back here at
//      all — but we cover the case where the email signed up with
//      a different address than the invitation was sent to.
//
//   2. Logged in: POST /v1/workspaces/accept-invite, then switch into
//      the new workspace and redirect to /app.
//
//   3. Token expired / consumed: show a clear error + back-to-login link.

import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Logo } from "../components/Logo";
import { useI18n } from "../lib/i18n";
import { useSession } from "../lib/session";
import { ApiError, workspaces } from "../lib/api";

const PENDING_KEY = "nexus.pendingInvite";

type State =
  | { kind: "missing_token" }
  | { kind: "waiting_for_login"; token: string }
  | { kind: "accepting" }
  | { kind: "accepted"; workspace_name: string }
  | { kind: "expired" }
  | { kind: "error"; msg: string };

function AcceptInvite() {
  const { t } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const search = useSearch({ from: "/accept-invite" }) as { token?: string };
  const ran = useRef(false);
  const [state, setState] = useState<State>({ kind: "accepting" });

  useEffect(() => {
    if (ran.current) return;
    if (s.state === "loading") return;
    ran.current = true;

    // Token resolution: prefer the URL param (fresh click), fall back
    // to sessionStorage (we just bounced through /login).
    const token =
      search.token?.trim() ||
      (typeof window !== "undefined" ? window.sessionStorage.getItem(PENDING_KEY) ?? "" : "");

    if (!token) {
      setState({ kind: "missing_token" });
      return;
    }

    if (s.state === "anon") {
      // Stash the token + bounce to /login. After sign-in the user
      // lands at /app/*, but they may also follow the link in the
      // email which now goes through this page logged-in.
      try {
        window.sessionStorage.setItem(PENDING_KEY, token);
      } catch { /* private mode etc. — just lose the convenience */ }
      setState({ kind: "waiting_for_login", token });
      return;
    }

    // Logged in — accept.
    workspaces.acceptInvitation(token)
      .then(async (r) => {
        try { window.sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
        // Switch the session into the newly-joined workspace + refresh
        // so the SPA header reflects it.
        try {
          await workspaces.switch({ workspace_id: r.workspace_id });
          await s.refresh();
        } catch { /* non-fatal — they can switch manually */ }
        setState({ kind: "accepted", workspace_name: r.workspace_name });
        // Auto-redirect after a brief success state so the user sees
        // the confirmation, then lands in the workspace.
        window.setTimeout(() => { void nav({ to: "/app", replace: true }); }, 1500);
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 410) {
          setState({ kind: "expired" });
        } else {
          setState({ kind: "error", msg: e instanceof Error ? e.message : String(e) });
        }
      });
  }, [s, search.token, nav]);

  if (s.state === "loading") return null;
  if (state.kind === "waiting_for_login") {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-full flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8 flex flex-col gap-5">
        <Link to="/login" className="ring-nexus rounded self-start"><Logo size="sm" /></Link>
        {state.kind === "accepting" && (
          <>
            <h1 className="font-display text-2xl font-semibold">{t("acceptInvite.accepting")}</h1>
            <p className="text-secondary text-sm">{t("acceptInvite.acceptingHint")}</p>
          </>
        )}
        {state.kind === "accepted" && (
          <>
            <h1 className="font-display text-2xl font-semibold text-success">
              ✓ {t("acceptInvite.accepted")}
            </h1>
            <p className="text-secondary text-sm">
              {t("acceptInvite.acceptedHint").replace("{workspace}", state.workspace_name)}
            </p>
            <Link to="/app"><Button className="w-full">{t("acceptInvite.continue")}</Button></Link>
          </>
        )}
        {state.kind === "expired" && (
          <>
            <h1 className="font-display text-2xl font-semibold text-error">
              {t("acceptInvite.expired")}
            </h1>
            <p className="text-secondary text-sm">{t("acceptInvite.expiredHint")}</p>
            <Link to="/login"><Button className="w-full" variant="secondary">
              {t("acceptInvite.backToLogin")}
            </Button></Link>
          </>
        )}
        {state.kind === "missing_token" && (
          <>
            <h1 className="font-display text-2xl font-semibold text-error">
              {t("acceptInvite.missingToken")}
            </h1>
            <p className="text-secondary text-sm">{t("acceptInvite.missingTokenHint")}</p>
            <Link to="/login"><Button className="w-full" variant="secondary">
              {t("acceptInvite.backToLogin")}
            </Button></Link>
          </>
        )}
        {state.kind === "error" && (
          <>
            <h1 className="font-display text-2xl font-semibold text-error">{t("common.errorGeneric")}</h1>
            <p className="text-secondary text-sm font-mono">{state.msg}</p>
            <Link to="/login"><Button className="w-full" variant="secondary">
              {t("acceptInvite.backToLogin")}
            </Button></Link>
          </>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/accept-invite")({
  component: AcceptInvite,
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
});
